import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'

import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import { navigation, contact, social, siteConfig, author } from '@/constants/site'

/** Static so the server and client never disagree during hydration. */
const COPYRIGHT_YEAR = 2026

export const SiteFooter: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            // The footer itself carries `data-reveal`, and querySelectorAll only
            // ever returns descendants, so the root has to be unhidden by name.
            // Missing it leaves the whole footer at visibility: hidden for good.
            gsap.set([element, ...element.querySelectorAll('[data-reveal]')], {
                visibility: 'visible'
            })

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            gsap.fromTo(
                element.querySelectorAll('[data-footer-row]'),
                { y: 34, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: 'power3.out',
                    stagger: 0.08,
                    scrollTrigger: { trigger: element, start: 'top 82%', once: true }
                }
            )

            // The wordmark rises last and travels furthest, so the footer lands
            // on it rather than arriving all at once.
            gsap.fromTo(
                element.querySelector('[data-footer-mark]'),
                { yPercent: 26, opacity: 0 },
                {
                    yPercent: 0,
                    opacity: 1,
                    duration: 1.5,
                    ease: 'expo.out',
                    scrollTrigger: { trigger: element, start: 'top 68%', once: true }
                }
            )
        }, element)

        return () => context.revert()
    }, [])

    return (
        <footer
            ref={root}
            data-reveal
            className='grain relative overflow-hidden bg-[color:var(--ink)] text-[color:var(--cream)]'
        >
            <div className='edge mx-auto max-w-[110rem] pb-8 pt-20 md:pt-28'>
                <div
                    data-footer-row
                    className='grid gap-12 border-b border-white/10 pb-14 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]'
                >
                    <div>
                        <p className='label text-white/40'>Let’s make it happen</p>

                        <p className='mt-4 max-w-md text-[0.9375rem] leading-relaxed text-white/60'>
                            Tell me what the day is, and I will tell you honestly whether I am
                            the right person to photograph it.
                        </p>

                        <a
                            href={`mailto:${contact.email}`}
                            className='display-lg mt-8 inline-block break-all text-[color:var(--gold)] transition-opacity duration-500 hover:opacity-70'
                        >
                            {contact.email}
                        </a>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-2'>
                        <nav aria-label='Footer'>
                            <p className='label mb-4 text-white/40'>Pages</p>

                            <ul className='space-y-2.5'>
                                {navigation.map((item) => (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            className='link-wipe text-[0.875rem] text-white/75'
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div>
                            <p className='label mb-4 text-white/40'>Studio</p>

                            <ul className='space-y-2.5 text-[0.875rem] text-white/75'>
                                <li>
                                    <a
                                        href={`tel:${contact.phone.replace(/\s/g, '')}`}
                                        className='link-wipe'
                                    >
                                        {contact.phone}
                                    </a>
                                </li>

                                <li className='text-white/50'>{contact.city}</li>

                                {social.map((item) => (
                                    <li key={item.label}>
                                        <a
                                            href={item.href}
                                            target='_blank'
                                            rel='noreferrer noopener'
                                            className='inline-flex items-center gap-1'
                                        >
                                            <span className='link-wipe'>{item.label}</span>
                                            <ArrowUpRight className='size-3' aria-hidden='true' />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div
                    data-footer-row
                    className='flex flex-wrap items-center justify-between gap-4 py-8 text-[0.6875rem] text-white/40'
                >
                    <p>
                        &copy; {COPYRIGHT_YEAR} {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        {author.name}, {author.role} {author.based}
                    </p>
                </div>
            </div>

            {/* Full-bleed wordmark, cropped at the baseline exactly as the
                reference sets it. */}
            <div className='overflow-hidden' aria-hidden='true'>
                <p
                    data-footer-mark
                    className='display-hero -mb-[0.12em] select-none whitespace-nowrap px-[2vw] text-center leading-[0.78] text-white/95'
                    style={{ fontSize: 'clamp(3rem, 19vw, 21rem)' }}
                >
                    {siteConfig.name.toUpperCase()}.
                </p>
            </div>
        </footer>
    )
}
